import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fdate, fbizdate, inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

const TONE = { active: "bg-emerald-50 text-emerald-700", expired: "bg-slate-100 text-slate-600",
  cancelled: "bg-rose-50 text-rose-700", exhausted: "bg-amber-50 text-amber-700" };

// Customers who bought one package plan, with Edit (validity/notes) and Cancel (stops future use).
export function PackageCustomers({ planId, canManage }) {
  const [act, setAct] = useState(null); // {kind, row}
  const { data, isLoading } = useQuery({ queryKey: ["pkg-customers", planId],
    queryFn: async () => (await api.get(`/package-plans/${planId}/customers`)).data });
  const rows = data?.items || [];
  if (isLoading) return <p className="text-xs text-slate-400 py-2">Loading…</p>;
  if (!rows.length) return <p className="text-xs text-slate-400 py-2" data-testid={`pkg-customers-empty-${planId}`}>No customer has bought this package yet.</p>;
  return (
    <div className="mt-3 overflow-x-auto" data-testid={`pkg-customers-${planId}`}>
      <div className="mb-2 flex flex-wrap gap-1.5 text-[11px]">
        {Object.entries(data.counts || {}).map(([k, n]) => <span key={k} className={`rounded px-1.5 py-0.5 capitalize ${TONE[k] || "bg-slate-100"}`}>{k} {n}</span>)}
      </div>
      <table className="w-full text-xs">
        <thead className="text-left text-slate-500"><tr><th className="py-1 pr-2">Customer</th><th className="pr-2">Phone</th><th className="pr-2">Sold</th><th className="pr-2">Expires</th><th className="pr-2">Credits</th><th className="pr-2">Status</th><th /></tr></thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`pkg-cust-row-${r.id}`}>
              <td className="py-1.5 pr-2 font-medium text-slate-800">{r.customer_name || "—"}</td>
              <td className="pr-2 text-slate-600">{r.customer_mobile || "—"}</td>
              <td className="pr-2 whitespace-nowrap">{fdate(r.created_at)}</td>
              <td className="pr-2 whitespace-nowrap">{r.expiry_date ? fbizdate(r.expiry_date) : "—"}</td>
              <td className="pr-2 tabular">{r.remaining_credits}/{r.total_credits}</td>
              <td className="pr-2"><span className={`rounded px-1.5 py-0.5 capitalize ${TONE[r.effective_status] || "bg-slate-100"}`} data-testid={`pkg-cust-status-${r.id}`}>{r.effective_status}</span></td>
              <td className="text-right">
                {canManage && r.status !== "cancelled" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button className="rounded p-1 hover:bg-slate-100" data-testid={`pkg-cust-menu-${r.id}`} aria-label="Actions"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem onClick={() => setAct({ kind: "edit", row: r })} data-testid={`pkg-cust-edit-${r.id}`}>Edit validity</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setAct({ kind: "cancel", row: r })} className="text-rose-600" data-testid={`pkg-cust-cancel-${r.id}`}>Cancel package</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {act && <PackageActionDialog act={act} planId={planId} onClose={() => setAct(null)} />}
    </div>
  );
}

function PackageActionDialog({ act, planId, onClose }) {
  const qc = useQueryClient();
  const r = act.row, isCancel = act.kind === "cancel";
  const [expiry, setExpiry] = useState((r.expiry_date || "").slice(0, 10));
  const [notes, setNotes] = useState(r.notes || "");
  const [reason, setReason] = useState("");
  const m = useMutation({
    mutationFn: () => (isCancel ? api.post(`/packages/${r.id}/cancel`, { reason })
      : api.patch(`/packages/${r.id}`, { expiry_date: expiry, notes, reason })),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pkg-customers", planId] }); toast.success(isCancel ? "Package cancelled. No refund was issued." : "Package updated"); onClose(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid={isCancel ? "pkg-cancel-dialog" : "pkg-edit-dialog"}>
        <DialogHeader><DialogTitle className="font-display">{isCancel ? "Cancel package?" : "Edit package"}</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-slate-600">
          {r.customer_name} · {r.plan_name} · {r.remaining_credits}/{r.total_credits} credits left · sold for {inr(r.price)}
        </DialogDescription>
        {isCancel ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">The remaining credits can no longer be used. The sale, invoice, payments and usage history stay unchanged. No money is refunded here; use the invoice refund flow if needed.</p>
        ) : (
          <div className="space-y-2">
            <label className="block text-xs text-slate-600">Valid until<Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="pkg-edit-expiry" className="mt-1" /></label>
            <label className="block text-xs text-slate-600">Notes<Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pkg-edit-notes" className="mt-1" /></label>
            <p className="text-[11px] text-slate-500">Credits, price and services are locked after a sale.</p>
          </div>
        )}
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="pkg-action-reason" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Back</Button>
          <Button onClick={() => m.mutate()} disabled={!reason.trim() || m.isPending} data-testid="pkg-action-confirm"
            className={isCancel ? "bg-rose-600 text-white hover:bg-rose-700" : "bg-slate-900 text-white"}>{m.isPending ? "Saving…" : isCancel ? "Cancel package" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
